import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { TIERS } from '../lib/tiers.js';
import { useAuth } from '../auth/AuthContext.js';

interface Person {
  id: string; email: string | null; displayName: string | null; role: string; tier: string;
  relation: string | null; pending: boolean; hasInvite: boolean; contact: boolean;
  organizationId: string | null;
  activatedAt: number | null; revokedAt: number | null;
}
interface Org { id: string; name: string }

const ROLES = ['invited', 'trustee', 'public'] as const;

export function PeoplePane() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { data, isLoading, error } = useQuery<{ people: Person[] }>({
    queryKey: ['people'],
    queryFn: () => api('/api/people'),
  });
  const orgsQ = useQuery<{ organizations: Org[] }>({ queryKey: ['organizations'], queryFn: () => api('/api/organizations') });
  const orgs = orgsQ.data?.organizations ?? [];

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');
  const [password, setPassword] = useState('');
  const [tier, setTier] = useState<(typeof TIERS)[number]>('family');
  const [role, setRole] = useState<(typeof ROLES)[number]>('invited');
  const [lastInvite, setLastInvite] = useState<{ email: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // Inline edit of a person's name + relation.
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editRelation, setEditRelation] = useState('');

  function resetForm() { setEmail(''); setName(''); setRelation(''); setPassword(''); }

  async function invite() {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await api<{ inviteToken: string; acceptUrl: string }>('/api/people/invite', {
        method: 'POST',
        body: JSON.stringify({ email, displayName: name || undefined, relation: relation || undefined, tier, role }),
      });
      setLastInvite({ email, url: `${window.location.origin}${r.acceptUrl}` });
      resetForm();
      qc.invalidateQueries({ queryKey: ['people'] });
    } finally { setBusy(false); }
  }

  // Add a person without sending an invite (email optional → a contact).
  // Optionally set a password so they can sign in directly (requires an email).
  async function add() {
    if (!name.trim() && !email.trim() && !relation.trim()) return;
    if (password.trim() && !email.trim()) { alert('A password requires an email to sign in with.'); return; }
    setBusy(true);
    try {
      await api('/api/people', {
        method: 'POST',
        body: JSON.stringify({ email: email || undefined, displayName: name || undefined, relation: relation || undefined, password: password || undefined, tier, role }),
      });
      setLastInvite(null);
      resetForm();
      qc.invalidateQueries({ queryKey: ['people'] });
    } finally { setBusy(false); }
  }
  async function patch(id: string, body: Record<string, string | null>) {
    await api(`/api/people/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    qc.invalidateQueries({ queryKey: ['people'] });
  }
  async function revoke(id: string) {
    await api(`/api/people/${id}/revoke`, { method: 'POST' });
    qc.invalidateQueries({ queryKey: ['people'] });
  }
  async function reinvite(id: string, email: string) {
    const r = await api<{ acceptUrl: string }>(`/api/people/${id}/reinvite`, { method: 'POST' });
    setLastInvite({ email, url: `${window.location.origin}${r.acceptUrl}` });
    qc.invalidateQueries({ queryKey: ['people'] });
  }
  async function del(p: Person) {
    if (!window.confirm(`Delete ${p.displayName ?? p.email ?? 'this person'} and all their conversations? This cannot be undone.`)) return;
    await api(`/api/people/${p.id}`, { method: 'DELETE' });
    qc.invalidateQueries({ queryKey: ['people'] });
  }
  function startEdit(p: Person) { setEditId(p.id); setEditName(p.displayName ?? ''); setEditRelation(p.relation ?? ''); }
  async function saveEdit(id: string) {
    await patch(id, { displayName: editName, relation: editRelation });
    setEditId(null);
  }

  return (
    <>
      <div className="mb-4">
        <h3 className="font-serif text-2xl text-ink m-0">People & access</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          Invite family, friends and colleagues to converse with you. Each person gets a visibility
          tier — they only ever see memories at or below their level. Trustees can activate legacy mode.
        </p>
      </div>

      {!isOwner && (
        <div className="mb-4 px-3 py-2 rounded-lg border border-amber/30 bg-amber/[0.05] text-[11.5px] text-amber/85">
          Read-only — only the owner can add, edit or remove people.
        </div>
      )}

      {/* Invite form — owner only */}
      {isOwner && (
      <div className="rounded-xl border border-ink/10 p-4 mb-5 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email (optional)"
            className="px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="display name"
            className="px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
          <input value={relation} onChange={(e) => setRelation(e.target.value)} placeholder="relation (e.g. daughter)"
            className="px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="new-password" placeholder="password (optional, ≥6 — lets them sign in)"
            className="px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
          <div className="flex gap-2">
            <select value={tier} onChange={(e) => setTier(e.target.value as typeof tier)}
              className="flex-1 px-2 py-2 rounded-lg border border-ink/15 bg-void text-[11px] text-ink/70">
              {TIERS.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
            <select value={role} onChange={(e) => setRole(e.target.value as typeof role)}
              className="px-2 py-2 rounded-lg border border-ink/15 bg-void text-[11px] text-ink/70">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={add} disabled={busy || (!name.trim() && !email.trim() && !relation.trim())}
            className="px-4 py-2 rounded-full border border-ink/20 text-ink/80 text-[11.5px] hover:border-ink/40 hover:text-ink disabled:opacity-30">
            {busy ? '…' : 'Add'}
          </button>
          <button onClick={invite} disabled={busy || !email.trim()}
            className="px-4 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
          <span className="font-serif italic text-[12px] text-ink/40">Add = contact (set a password to let them sign in); Send invite = email magic link</span>
        </div>
        {lastInvite && (
          <div className="px-3 py-2 rounded-lg bg-amber/[0.06] border-l-2 border-amber font-mono text-[11px] text-ink/70 break-all">
            Invite link for <span className="text-amber">{lastInvite.email}</span> (dev — would be emailed):<br />
            <span className="text-ink">{lastInvite.url}</span>
          </div>
        )}
      </div>
      )}

      {/* People list */}
      {isLoading ? <div className="text-ink/40 text-[12px] py-6">Loading…</div>
        : error ? <div className="text-red-300 text-[12px] py-6">Couldn’t load people.</div>
        : (
        <ul className="flex flex-col gap-1">
          {data!.people.map((p) => (
            <li key={p.id} className="flex flex-col gap-2 py-2.5 px-3 rounded-lg border border-ink/[0.06]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full grid place-items-center font-serif italic text-sm text-ink bg-gradient-to-br from-amber/50 to-amber/10 flex-shrink-0">
                  {(p.displayName ?? p.email ?? '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-[14px] text-ink truncate">{p.displayName ?? p.email}
                    <span className="text-ink/40 text-[12px]"> · {p.relation ?? '—'}</span></div>
                  <div className="text-[11px] text-ink/40">
                    {p.email ?? 'no email'} · {p.role}
                    {p.revokedAt ? ' · revoked' : p.pending ? ' · invite pending' : p.activatedAt ? ' · active' : p.contact ? ' · contact' : ''}
                  </div>
                </div>
                {isOwner && p.role !== 'owner' && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <select value={p.tier} onChange={(e) => patch(p.id, { tier: e.target.value })}
                      className="px-1.5 py-1 rounded border border-ink/15 bg-void text-[11px] text-ink/70">
                      {TIERS.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                    </select>
                    <select value={p.role} onChange={(e) => patch(p.id, { role: e.target.value })}
                      className="px-1.5 py-1 rounded border border-ink/15 bg-void text-[11px] text-ink/70">
                      {['invited', 'trustee', 'public'].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    {orgs.length > 0 && (
                      <select value={p.organizationId ?? ''} onChange={(e) => patch(p.id, { organizationId: e.target.value || null })}
                        title="Organization (professional layer)"
                        className="px-1.5 py-1 rounded border border-ink/15 bg-void text-[11px] text-ink/70 max-w-[110px]">
                        <option value="">— org —</option>
                        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                    )}
                    {p.pending || p.revokedAt || p.contact
                      ? <button onClick={() => reinvite(p.id, p.email ?? p.displayName ?? 'contact')} className="text-[11px] text-amber/80 hover:text-amber px-1">invite</button>
                      : <button onClick={() => revoke(p.id)} className="text-[11px] text-red-300/70 hover:text-red-300 px-1">revoke</button>}
                    <button onClick={() => startEdit(p)} title="Edit name / relation" className="text-ink/40 hover:text-ink px-1 text-[13px]">✎</button>
                    <button onClick={() => del(p)} title="Delete person" className="text-ink/40 hover:text-red-300 px-1 text-[13px]">🗑</button>
                  </div>
                )}
                {p.role === 'owner' && <span className="text-[11px] text-particle px-2">owner</span>}
              </div>

              {isOwner && editId === p.id && (
                <div className="flex items-center gap-2 pl-11">
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="display name"
                    className="flex-1 px-2.5 py-1.5 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[13px] text-ink focus:border-ink/40 focus:outline-none" />
                  <input value={editRelation} onChange={(e) => setEditRelation(e.target.value)} placeholder="relation"
                    className="flex-1 px-2.5 py-1.5 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[13px] text-ink focus:border-ink/40 focus:outline-none" />
                  <button onClick={() => saveEdit(p.id)} className="px-3 py-1.5 rounded-full bg-ink text-void text-[11px]">Save</button>
                  <button onClick={() => setEditId(null)} className="text-[11px] text-ink/40 hover:text-ink px-1">Cancel</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
